import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Home,
  MessageSquare,
  BookOpen,
  MoreHorizontal
} from 'lucide-react';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  isActive: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, badge, isActive }) => {
  return (
    <Link
      to={to}
      className={`
        relative flex flex-col items-center justify-center gap-0.5
        flex-1 py-2
        transition-all duration-200
        ${isActive 
          ? 'text-blue-600' 
          : 'text-ink-400 active:text-ink-600'
        }
      `}
    >
      {/* Active indicator dot */}
      {isActive && (
        <span className="absolute top-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full" />
      )}
      
      <span className={`
        relative transition-transform duration-200
        ${isActive ? 'scale-110' : ''}
      `}>
        {icon}
        {/* Badge */}
        {badge !== undefined && badge > 0 && (
          <span className="
            absolute -top-1 -right-1.5
            min-w-[16px] h-4 px-1
            bg-gradient-to-br from-rose-500 to-pink-600
            text-white text-9 font-bold
            rounded-full flex items-center justify-center
            shadow-sm ring-2 ring-white
          ">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      
      <span className={`
        text-2xs font-medium
        transition-all duration-200
        ${isActive ? 'text-blue-600 font-semibold' : ''}
      `}>
        {label}
      </span>
    </Link>
  );
};

interface MobileBottomNavProps {
  onMoreClick: () => void;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onMoreClick }) => {
  const location = useLocation();
  const { requestsBadgeCount, bookingsBadgeCount } = useNotifications();
  const { canManageRequests } = usePermissions();

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Build active paths for "More" detection based on visible nav items
  const mainNavPaths = ['/dashboard', '/bookings'];
  if (canManageRequests) mainNavPaths.push('/requests');

  const isMoreActive = !mainNavPaths.some(
    path => location.pathname === path || location.pathname.startsWith(path + '/')
  );

  return (
    <nav className="
      fixed bottom-0 left-0 right-0 z-40
      md:hidden
      bg-surface/90 backdrop-blur-xl
      border-t border-ink-200/60
      safe-area-bottom
    ">
      {/* Subtle top shadow/glow */}
      <div className="absolute inset-x-0 -top-4 h-4 bg-gradient-to-t from-black/5 to-transparent pointer-events-none" />

      <div className="flex items-stretch h-16 max-w-lg mx-auto px-2">
        <NavItem
          to="/dashboard"
          icon={<Home size={22} strokeWidth={isActive('/dashboard') ? 2.5 : 2} />}
          label="Home"
          isActive={isActive('/dashboard')}
        />

        {canManageRequests && (
          <NavItem
            to="/requests"
            icon={<MessageSquare size={22} strokeWidth={isActive('/requests') ? 2.5 : 2} />}
            label="Requests"
            badge={requestsBadgeCount}
            isActive={isActive('/requests')}
          />
        )}

        <NavItem
          to="/bookings"
          icon={<BookOpen size={22} strokeWidth={isActive('/bookings') ? 2.5 : 2} />}
          label="Bookings"
          badge={bookingsBadgeCount}
          isActive={isActive('/bookings')}
        />

        {/* More button opens the full menu */}
        <button
          onClick={onMoreClick}
          className={`
            relative flex flex-col items-center justify-center gap-0.5
            flex-1 py-2
            transition-all duration-200
            ${isMoreActive
              ? 'text-blue-600'
              : 'text-ink-400 active:text-ink-600'
            }
          `}
        >
          {isMoreActive && (
            <span className="absolute top-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full" />
          )}
          <span className={`transition-transform duration-200 ${isMoreActive ? 'scale-110' : ''}`}>
            <MoreHorizontal size={22} strokeWidth={isMoreActive ? 2.5 : 2} />
          </span>
          <span className={`
            text-2xs font-medium
            ${isMoreActive ? 'text-blue-600 font-semibold' : ''}
          `}>
            More
          </span>
        </button>
      </div>
    </nav>
  );
};

export default MobileBottomNav;

